"use client";

import { Check, ChevronsUpDown } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TICKER_CONFIG, type TickerCategory, type TickerMeta } from "@/lib/simulator/data/tickers";
import { cn } from "@/lib/utils";

/* ──────────────────────────────────────────────────────────────────────────
 * TickerSelect — searchable, grouped ticker combobox.
 *
 * Collapsed state: shows just the symbol (compact, matches the rest of the
 * scenario strip's tabular-num value styling).
 *
 * Open state: searchable list with two-line entries (symbol bold + name
 * below), grouped by `TickerCategory` from `tickers.ts`. Search matches
 * against symbol AND name (CommandItem `value` includes both).
 * ────────────────────────────────────────────────────────────────────────── */

export interface TickerSelectProps {
  value: string;
  onChange: (ticker: string) => void;
  disabled?: boolean;
}

type Group = { category: TickerCategory; tickers: TickerMeta[] };

// Group ticker entries by category, preserving the order they appear in
// TICKER_CONFIG. The config's source order is the dropdown's display order.
const GROUPS: readonly Group[] = (() => {
  const out: Group[] = [];
  for (const meta of Object.values(TICKER_CONFIG)) {
    const existing = out.find((g) => g.category === meta.category);
    if (existing) existing.tickers.push(meta);
    else out.push({ category: meta.category, tickers: [meta] });
  }
  return out;
})();

export function TickerSelect({ value, onChange, disabled }: TickerSelectProps) {
  const [open, setOpen] = React.useState(false);
  const selected = TICKER_CONFIG[value];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Select ticker"
          disabled={disabled}
          className="font-display h-9 w-full justify-between text-lg font-medium tabular-nums"
        >
          <span className="truncate">{selected?.ticker ?? value}</span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command
          // Combine symbol + name into one search corpus so users can type
          // either. The default Command filter does fuzzy substring matching.
          filter={(itemValue, search) => {
            const needle = search.toLowerCase();
            return itemValue.toLowerCase().includes(needle) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search tickers or names..." />
          <CommandList>
            <CommandEmpty>No ticker found.</CommandEmpty>
            {GROUPS.map((group) => (
              <CommandGroup key={group.category} heading={group.category}>
                {group.tickers.map((t) => (
                  <CommandItem
                    key={t.ticker}
                    value={`${t.ticker} ${t.name}`}
                    onSelect={() => {
                      onChange(t.ticker);
                      setOpen(false);
                    }}
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="font-medium tabular-nums">{t.ticker}</span>
                      <span className="text-muted-foreground truncate text-xs">{t.name}</span>
                    </div>
                    <Check
                      className={cn(
                        "ml-auto size-4 shrink-0",
                        value === t.ticker ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
